/*
 * Copyright (c) 2026. Tooling Team
 */

import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Popover from "react-bootstrap/Popover";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { regular, solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import moment from "moment";
import { formatLongDescription } from "../components/table/LongDescriptionCellRenderer";
import Layout from "../components/layout/Layout";
import { Button } from "@uhg-abyss/web/ui/Button";
import { Alert } from "@uhg-abyss/web/ui/Alert";
import { Link } from "@uhg-abyss/web/ui/Link";
import { Card } from "@uhg-abyss/web/ui/Card";
import { PageBodyIntro } from "@uhg-abyss/web/ui/PageBodyIntro";
import { PageBody } from "@uhg-abyss/web/ui/PageBody";
import { Text } from "@uhg-abyss/web/ui/Text";
import { RouteDisplay } from "../components/navigation/RouteDisplay";
import { CONCEPTS_SUFFIX, PATHS } from "../utils/constants";
import { Tabs } from "@uhg-abyss/web/ui/Tabs";
import { TextInput } from "@uhg-abyss/web/ui/TextInput";
import {
  useCreateClientMutation,
  useGetClientListQuery,
  useRetireClientMutation,
  useUpdateClientMutation,
} from "../app/services/clientApi";
import { useState, useMemo, useEffect, useCallback, useReducer } from "react";
import { FileUpload } from "@uhg-abyss/web/ui/FileUpload";
import { FormProvider } from "@uhg-abyss/web/ui/FormProvider";
import { useForm } from "@uhg-abyss/web/hooks/useForm";
import { useToast } from "@uhg-abyss/web/hooks/useToast";
import { DataTable, EditableTableCell } from "@uhg-abyss/web/ui/DataTable";
import { ModalDialog } from "@uhg-abyss/web/ui/ModalDialog";
import { useDataTable } from "@uhg-abyss/web/hooks/useDataTable";
import { downloadCsv } from "@uhg-abyss/web/tools/downloadCsv";
import {
  useCreateConceptSetMutation,
  useGetConceptSetsQuery,
  useLazySearchConceptSetsByConditionQuery,
  useSearchConceptSetsByConditionQuery,
  useUpdateConceptSetMutation,
} from "../app/services/conceptSetsApi";
import { isAdminUser } from "../utils/authz";

export const VIEW_CONCEPT_SET_COLUMNS = [
  { header: "Code", accessorKey: "code" },
  { header: "Description", accessorKey: "description" },
  {
    header: "Long Description",
    accessorKey: "longDescription",
    enableColumnFilter: false,
    enableSorting: false,
    size: 120,
    minSize: 120,
    maxSize: 120,
    cell: (cell) => {
      const longDesc = cell.getValue();
      if (!longDesc) return null;
      const description = cell.row.original.description || "None";
      const popover = (
        <Popover id="concept-long-desc-popover" style={{ zIndex: 1090 }}>
          <Popover.Header as="h3">Description</Popover.Header>
          <Popover.Body style={{ maxWidth: "350px" }}>
            {description}
          </Popover.Body>
          <Popover.Header as="h3">JSON Data</Popover.Header>
          <Popover.Body style={{ maxWidth: "350px" }}>
            <span>{formatLongDescription(longDesc)}</span>
          </Popover.Body>
        </Popover>
      );
      return (
        <div
          className="localDescIcon mt-2andaBit fa-lg localDescIconCustom"
          style={{
            display: "flex",
            flex: 1,
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <OverlayTrigger
            container={document.body}
            trigger={["hover", "focus"]}
            placement="right"
            overlay={popover}
          >
            <div>
              <FontAwesomeIcon icon={regular("rectangle-list")} />
            </div>
          </OverlayTrigger>
        </div>
      );
    },
  },
  { header: "System", accessorKey: "system" },
];

export const DOWNLOAD_CONCEPT_SET_COLUMNS = [
  { header: "CODE", accessorKey: "code" },
  { header: "DESCRIPTION", accessorKey: "description" },
  { header: "CONCEPT_SET", accessorKey: "conceptSet" },
  { header: "LONG_DESCRIPTION", accessorKey: "longDescription" },
  { header: "SYSTEM", accessorKey: "system" },
  { header: "VERSION", accessorKey: "version" },
];

export const getConceptSetColumns = ({ onView, onEdit, onDownload }) => [
  {
    header: "Domain",
    accessorKey: "domain",
  },
  {
    header: "System",
    accessorKey: "system",
  },
  {
    header: "Definitions",
    accessorKey: "totalDefinitionCount",
    size: 150,
    minSize: 150,
    maxSize: 150,
    meta: { textAlign: "right" },
  },
  {
    header: "Actions",
    id: "actions",
    enableColumnFilter: false,
    enableSorting: false,
    size: 320,
    minSize: 320,
    maxSize: 320,
    cell: ({ row }) => {
      return (
        <div className="d-flex gap-2 justify-content-center">
          <Button
            aria-label="Replace definitions in concept set"
            title="Replace definitions in concept set"
            icon={{
              icon: "upload",
              position: "leading",
              variant: "outlined",
            }}
            onClick={() => onEdit?.(row.original)}
            size="sm"
            variant="outline"
            color="neutral"
          >
            Replace Defs
          </Button>
          <Button
            aria-label="Preview concept set"
            onClick={() => onView?.(row.original)}
            size="sm"
            variant="outline"
            color="neutral"
          >
            Preview
          </Button>
          <Button
            aria-label="Download  Definitions CSV"
            title="Download  Definitions CSV"
            icon={{
              icon: "download",
              position: "icon-only",
              variant: "outlined",
            }}
            onClick={() => onDownload?.(row.original)}
            size="sm"
            variant="outline"
            color="neutral"
          />
        </div>
      );
    },
  },
];

export const buildConceptSetTableRows = (
  conceptSets = [],
  totalDefinitionCounts = {}
) =>
  conceptSets.map((conceptSet) => ({
    ...conceptSet,
    domain: conceptSet.name?.replace(CONCEPTS_SUFFIX, ""),
    totalDefinitionCount: totalDefinitionCounts[conceptSet._id] ?? "-",
  }));

export const totalDefinitionCountsReducer = (state, action) => {
  if (action.type === "reset") return {};

  return {
    ...state,
    [action.conceptSetId]: action.totalDefinitionCount,
  };
};

const renderEditableClientCell = (cell, emptyValue = "") => {
  const editActions = cell.table?.options?.meta?.editActions;
  const isRowInEditMode = editActions?.rowsInEditMode?.[cell.row?.id];

  if (isRowInEditMode) {
    return (
      <EditableTableCell
        {...cell}
        getValue={() => cell.getValue?.() ?? ""}
        inputType="text"
      />
    );
  }

  return cell.getValue?.() || emptyValue;
};

export const getClientColumns = ({ onSave, onRetire } = {}) => [
  {
    header: "Name",
    accessorKey: "name",
    cell: (cell) => renderEditableClientCell(cell),
  },
  {
    header: "Description",
    accessorKey: "description",
    cell: (cell) => renderEditableClientCell(cell, "None"),
  },
  {
    header: "Last Updated",
    accessorKey: "updated_at",
    size: 250,
    minSize: 250,
    maxSize: 250,
    cell: (cell) => {
      const value = cell.getValue();
      if (!value) return "";

      const updatedAt = moment(value);
      return updatedAt.isValid() ? updatedAt.format("MM-DD-YYYY HH:mm:ss") : "";
    },
  },
  {
    header: "Actions",
    id: "actions",
    enableColumnFilter: false,
    enableSorting: false,
    size: 200,
    minSize: 200,
    maxSize: 200,
    cell: (cell) => {
      const { row, table } = cell;
      const editActions = table.options.meta?.editActions;
      const isEditing = Boolean(editActions?.rowsInEditMode?.[row.id]);

      const exitEditMode = () => {
        editActions?.setRowsInEditMode((previousRows) => {
          const nextRows = { ...previousRows };
          delete nextRows[row.id];
          return nextRows;
        });
      };

      const cancelEdit = () => {
        editActions?.revertChanges(row.index, row.id);
        exitEditMode();
      };

      const saveEdit = async () => {
        const previousRow = editActions?.getRowData(row.index);
        const updatedRow = editActions?.getTempEditedData(row.index);

        try {
          await onSave?.(previousRow, updatedRow);
          editActions?.saveChanges(row.index, row.id);
          exitEditMode();
        } catch (_error) {
          // Keep the row in edit mode.
        }
      };

      if (isEditing) {
        return (
          <div className="d-flex gap-2 justify-content-center">
            <Button
              aria-label="Save client changes"
              title="Save client changes"
              icon={{
                icon: "check",
                position: "icon-only",
                variant: "outlined",
              }}
              onClick={saveEdit}
              size="sm"
              variant="outline"
            />
            <Button
              aria-label="Cancel client changes"
              title="Cancel client changes"
              icon={{
                icon: "close",
                position: "icon-only",
                variant: "outlined",
              }}
              onClick={cancelEdit}
              size="sm"
              variant="outline"
            />
          </div>
        );
      }

      return (
        <div className="d-flex gap-2 justify-content-center">
          <Button
            aria-label="Edit client"
            color="neutral"
            icon={{
              icon: "edit",
              position: "leading",
              variant: "outlined",
            }}
            isDisabled={row.original.is_retired}
            onClick={() => editActions?.setRowsInEditMode({ [row.id]: true })}
            size="sm"
            variant="outline"
          >
            Edit
          </Button>
          <Button
            aria-label="Retire client"
            className="client-retire-action"
            color="destructive"
            isDisabled={row.original.is_retired}
            onClick={() => onRetire?.(row.original)}
            size="sm"
            variant="outline"
          >
            Retire
          </Button>
        </div>
      );
    },
  },
];

export const CLIENT_COLUMNS = getClientColumns();

const INITIAL_CREATE_CLIENT_FORM_VALUES = {
  clientName: "",
  description: "",
};

export const getConceptDomainDropdownValues = (conceptSetsData = []) => {
  const domainsByName = new Map();

  conceptSetsData.forEach((concept) => {
    if (!domainsByName.has(concept.domain)) {
      domainsByName.set(concept.domain, concept.domain);
    }
  });

  return Array.from(domainsByName.values())
    .sort((a, b) => a.localeCompare(b))
    .map((domain, index) => ({
      id: index,
      label: domain,
      value: domain,
    }));
};

export const getConceptSystemDropdownValues = (
  conceptSetsData = [],
  selectedDomain
) => {
  if (!selectedDomain) {
    return [];
  }

  return conceptSetsData
    .filter((concept) => concept.domain === selectedDomain.value)
    .map((concept, index) => ({
      id: index,
      label: concept.system,
      value: concept.conceptSetId,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

export const buildEditConceptSetUpdateRequest = (
  file,
  selectedDomain,
  selectedSystem
) => ({
  conceptSetId: selectedSystem.value,
  payload: {
    csvFile: file,
    domain: selectedDomain.value,
    codeSystem: selectedSystem.label,
  },
});

export const isEditConceptSetSelectionComplete = (
  selectedDomain,
  selectedSystem
) =>
  Boolean(
    selectedDomain?.value && selectedSystem?.value && selectedSystem?.label
  );

export const buildConceptSetSearchCondition = (
  selectedDomain,
  selectedSystem
) => {
  if (!selectedDomain?.value || !selectedSystem?.label) return null;

  return {
    criteria: {
      searchExpression: [
        {
          and: [
            { eq: { name: `${selectedDomain.value}_conceptset` } },
            { eq: { system: selectedSystem.label } },
          ],
        },
      ],
    },
  };
};

export const buildConceptSetDownloadRows = (rows, domainValue) =>
  rows.map((row) => ({
    ...row,
    conceptSet: domainValue,
    version: "",
  }));

// Shared by any tab that needs to search concept sets by domain/system selection
const useConceptSetSearchResults = (selectedDomain, selectedSystem) => {
  const condition = useMemo(
    () => buildConceptSetSearchCondition(selectedDomain, selectedSystem),
    [selectedDomain, selectedSystem]
  );

  const { data, isFetching } = useSearchConceptSetsByConditionQuery(condition, {
    skip: !condition,
  });

  const rows = useMemo(() => data?.[0]?.values ?? [], [data]);

  return { condition, rows, isFetching };
};

export const getConceptToastUpdate = (
  results,
  {
    isError = false,
    domainValue,
    systemValue,
    defaultSuccessMessage = "Concept set updated successfully.",
  } = {}
) => {
  if (isError) {
    return {
      title: "Error",
      type: "error",
      message:
        results?.data?.message ||
        results?.data?.detail ||
        results?.message ||
        results?.detail ||
        "Unknown error",
      autoClose: false,
    };
  }

  return {
    title: `Success for ${domainValue} and ${systemValue}`,
    type: "success",
    message:
      results?.message || results?.data?.message || defaultSuccessMessage,
    autoClose: false,
  };
};

export function AdminManagement() {
  const isAdmin = isAdminUser();

  // Create Client state and handlers
  const [createClientMutation] = useCreateClientMutation();
  const [createClientFormValues, setCreateClientFormValues] = useState(
    INITIAL_CREATE_CLIENT_FORM_VALUES
  );
  const [createClientErrors, setCreateClientErrors] = useState({});
  const [isCreateClientSubmitting, setIsCreateClientSubmitting] =
    useState(false);
  const [isCreateClientModalOpen, setIsCreateClientModalOpen] = useState(false);

  const closeCreateClientModal = () => {
    setCreateClientFormValues(INITIAL_CREATE_CLIENT_FORM_VALUES);
    setCreateClientErrors({});
    setIsCreateClientSubmitting(false);
    setIsCreateClientModalOpen(false);
  };

  const handleCreateClientChange = (field) => (event) => {
    const value = event.target.value;
    setCreateClientFormValues((previousState) => ({
      ...previousState,
      [field]: value,
    }));

    if (createClientErrors[field]) {
      setCreateClientErrors((previousErrors) => ({
        ...previousErrors,
        [field]: undefined,
      }));
    }
  };

  const validateCreateClientForm = () => {
    const validationErrors = {};
    const trimmedClientName = createClientFormValues.clientName
      .trim()
      .toLowerCase();

    if (!trimmedClientName) {
      validationErrors.clientName = "Client name is required.";
    }

    return validationErrors;
  };

  const handleCreateClientSubmit = async (event) => {
    event.preventDefault();

    const validationErrors = validateCreateClientForm();
    if (Object.keys(validationErrors).length > 0) {
      setCreateClientErrors(validationErrors);
      return;
    }

    setIsCreateClientSubmitting(true);

    try {
      const requestBody = {
        name: createClientFormValues.clientName.trim().toLowerCase(),
        description: createClientFormValues.description.trim(),
      };

      await createClientMutation(requestBody).unwrap();
      await refetchClientList?.();

      toast.show({
        title: "Client created",
        type: "success",
        message: `Client ${requestBody.name} was submitted successfully.`,
      });
      closeCreateClientModal();
    } catch (error) {
      let errorMessage =
        "There was a problem creating the client. Please try again.";

      if (error.status === 409) {
        errorMessage =
          "A client with this name already exists. Choose a unique name or edit the existing Client.";
      }
      toast.show({
        title: "Unable to create client",
        type: "error",
        message: errorMessage,
      });
      setCreateClientErrors({ clientName: errorMessage });
    } finally {
      setIsCreateClientSubmitting(false);
    }
  };

  const canCreateClientSubmit =
    createClientFormValues.clientName.trim() !== "" &&
    !isCreateClientSubmitting;

  // Edit Client state and handlers
  const [updateClientMutation] = useUpdateClientMutation();
  const [retireClientMutation] = useRetireClientMutation();
  const {
    data: clientListResponse,
    isLoading: isClientListLoading = false,
    isFetching: isClientListFetching = false,
    isError: isClientListError = false,
    refetch: refetchClientList,
  } = useGetClientListQuery(undefined, { skip: !isAdmin });

  const [clientPagination, setClientPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [clientGlobalFilter, setClientGlobalFilter] = useState("");
  const [isRetiringClient, setIsRetiringClient] = useState(false);
  const [clientPendingRetirement, setClientPendingRetirement] = useState(null);

  const handleGridRetireClient = useCallback((client) => {
    if (!client?._id || client.is_retired) return;
    setClientPendingRetirement(client);
  }, []);

  const confirmGridRetireClient = async () => {
    const client = clientPendingRetirement;
    if (!client?._id) return;

    setIsRetiringClient(true);
    try {
      await retireClientMutation({
        clientId: client._id,
        payload: { is_retired: true },
      }).unwrap();
      await refetchClientList?.();
      toast.show({
        title: "Client retired",
        type: "success",
        message: `${client.name} client retired successfully.`,
      });
    } catch (error) {
      toast.show({
        title: "Unable to retire client",
        type: "error",
        message:
          error?.data?.message ||
          error?.data?.detail ||
          "There was a problem retiring the client. Please try again.",
      });
    } finally {
      setIsRetiringClient(false);
      setClientPendingRetirement(null);
    }
  };

  const handleInlineClientEditCompleted = useCallback(
    async (previousRow, updatedRow) => {
      const previousName = String(previousRow?.name ?? "").trim();
      const previousDescription = String(previousRow?.description ?? "").trim();
      const hasUpdatedName = Object.prototype.hasOwnProperty.call(
        updatedRow || {},
        "name"
      );
      const hasUpdatedDescription = Object.prototype.hasOwnProperty.call(
        updatedRow || {},
        "description"
      );
      const updatedName = hasUpdatedName
        ? String(updatedRow.name ?? "")
            .trim()
            .toLowerCase()
        : previousName;
      const updatedDescription = hasUpdatedDescription
        ? String(updatedRow.description ?? "").trim()
        : previousDescription;

      if (hasUpdatedName && !updatedName) {
        const error = new Error("Client name is required.");
        toast.show({
          title: "Unable to update client",
          type: "error",
          message: error.message,
        });
        throw error;
      }

      const hasChanges =
        updatedName !== previousName ||
        updatedDescription !== previousDescription;

      if (!previousRow?._id || !hasChanges) return;

      const payload = {
        new_name: updatedName,
        description: updatedDescription,
      };

      try {
        await updateClientMutation({
          clientId: previousRow._id,
          payload,
        }).unwrap();
        await refetchClientList?.();
        toast.show({
          title: "Client updated",
          type: "success",
          message: `${previousRow.name} client was updated successfully.`,
        });
      } catch (error) {
        const detail = error?.data?.detail;
        const serverMessage =
          (typeof error?.data?.message === "string" && error.data.message) ||
          (Array.isArray(detail) &&
            detail
              .map((item) => (typeof item === "string" ? item : item?.msg))
              .filter(Boolean)
              .join(", ")) ||
          (typeof detail === "string" && detail) ||
          (typeof error?.message === "string" && error.message);
        toast.show({
          title: "Unable to update client",
          type: "error",
          message:
            serverMessage ||
            "There was a problem updating the client. Please check the edited values and try again.",
        });
        throw error;
      }
    },
    [refetchClientList, updateClientMutation]
  );

  const clientColumns = useMemo(
    () =>
      getClientColumns({
        onSave: handleInlineClientEditCompleted,
        onRetire: handleGridRetireClient,
      }),
    [handleGridRetireClient, handleInlineClientEditCompleted]
  );

  const clientDataTableProps = useDataTable({
    initialData: [],
    initialColumns: clientColumns,
    rowIdKey: "_id",
    tableConfig: {
      enableColumnFilters: false,
      enableSorting: true,
      initialState: {
        sorting: [{ id: "updated_at", desc: true }],
      },
      state: {
        pagination: clientPagination,
        globalFilter: clientGlobalFilter,
      },
      globalFilterFn: (row, _columnId, filterValue) => {
        const searchValue = String(filterValue ?? "")
          .trim()
          .toLowerCase();
        if (!searchValue) return true;

        const name = String(row.original?.name ?? "").toLowerCase();
        const description = String(
          row.original?.description ?? ""
        ).toLowerCase();

        return name.includes(searchValue) || description.includes(searchValue);
      },
      onPaginationChange: setClientPagination,
      onGlobalFilterChange: (nextGlobalFilter) => {
        setClientGlobalFilter((previousGlobalFilter) =>
          typeof nextGlobalFilter === "function"
            ? nextGlobalFilter(previousGlobalFilter)
            : nextGlobalFilter
        );
        setClientPagination((previousPagination) => ({
          ...previousPagination,
          pageIndex: 0,
        }));
      },
    },
    editCellConfig: {
      enableColumnEdit: false,
    },
    paginationConfig: {
      enablePagination: true,
      pageSizeOptions: [10, 25, 50],
    },
  });

  useEffect(() => {
    if (typeof clientDataTableProps.setData === "function") {
      clientDataTableProps.setData(clientListResponse || [], true);
    }
  }, [clientListResponse]);

  // --- Concept create state and handlers ---
  const [domain, setDomain] = useState("");
  const [system, setSystem] = useState("");
  const [createConceptApi] = useCreateConceptSetMutation();
  const [isCreateConceptSubmitting, setIsCreateConceptSubmitting] =
    useState(false);
  const [isCreateConceptUploading, setIsCreateConceptUploading] =
    useState(false);
  const [isCreateConceptModalOpen, setIsCreateConceptModalOpen] =
    useState(false);
  const createConceptForm = useForm();
  const { toast } = useToast();

  const conceptFileTypesAccepted = {
    "text/csv": [".csv"],
  };

  const resetCreateConceptState = () => {
    setDomain("");
    setSystem("");
    createConceptForm.reset();
    setIsCreateConceptSubmitting(false);
    setIsCreateConceptUploading(false);
  };

  const closeCreateConceptModal = () => {
    resetCreateConceptState();
    setIsCreateConceptModalOpen(false);
  };

  //Toast used for either edit or create concept
  const updateConceptToast = (results, options) => {
    toast.update({
      id: "concept-set",
      ...getConceptToastUpdate(results, options),
    });
    resetCreateConceptState();
    resetEditConceptState();
  };

  const createConcept = async (file) => {
    toast.show({
      id: "concept-set",
      isLoading: true,
      ariaLoadingLabel: "creating concept set",
      title: "Creating concept set",
      type: "info",
      message: "Uploading and processing data, please wait...",
      autoClose: false,
      withCloseButton: false,
    });
    setIsCreateConceptSubmitting(true);

    const requestBody = {
      domain,
      codeSystem: system,
      csvFile: file,
    };

    let results;
    try {
      results = await createConceptApi(requestBody).unwrap();
    } catch (err) {
      updateConceptToast(err, {
        isError: true,
        domainValue: domain,
        systemValue: system,
        defaultSuccessMessage: "Concept set created successfully.",
      });
      return;
    }

    setIsCreateConceptModalOpen(false);
    updateConceptToast(results, {
      isError: false,
      domainValue: domain,
      systemValue: system,
      defaultSuccessMessage: "Concept set created successfully.",
    });
  };

  const handleCreateConceptSubmit = (data) => {
    const file = data.fileUpload?.[0];
    if (!file || file.size === 0) {
      window.alert("Chosen file is empty- please upload a valid .csv file.");
      return;
    }
    setIsCreateConceptUploading(true);
    createConcept(file);
  };

  const renderCreateConceptForm = () => (
    <FormProvider
      state={createConceptForm}
      onSubmit={handleCreateConceptSubmit}
    >
      <Text className="mb-3" asElement="div">
        Input a Domain and System, then upload a file containing Concept
        Definitions, to create a new Concept.
      </Text>
      <div className="row">
        <div className="mt-auto" style={{ paddingBottom: "15px" }}>
          <TextInput
            label="Domain"
            validators={{ required: true }}
            placeholder="Domain"
            value={domain}
            isDisabled={isCreateConceptSubmitting}
            onChange={(event) => setDomain(event.target.value)}
          />
        </div>
        <div className="mt-auto">
          <TextInput
            placeholder="System"
            label="System"
            value={system}
            isDisabled={isCreateConceptSubmitting}
            onChange={(event) => setSystem(event.target.value)}
            validators={{ required: true }}
          />
        </div>
      </div>
      <div className="d-flex flex-wrap align-items-center gap-0 mb-3 mt-3">
        <span>Use the</span>
        <Button
          type="button"
          onClick={handleDownloadCreateConceptTemplate}
          color="brand"
          variant="text"
          isDisabled={!domain.trim() || !system.trim()}
        >
          Concept Set Template
        </Button>
        <span>if you are unsure of the required data and formatting.</span>
      </div>
      <div className="row">
        <div style={{ padding: "15px" }}>
          <FileUpload
            maxFiles={1}
            subText="Use .csv only"
            uploadMessage="Drag your .csv file here"
            fileTypes={conceptFileTypesAccepted}
            model="fileUpload"
            isUploading={isCreateConceptUploading}
            label="Upload Concept Definitions"
            isRequired
          />
        </div>
      </div>
      <div className="d-flex gap-2 mt-4">
        <Button
          type="submit"
          isLoading={isCreateConceptSubmitting}
          ariaLoadingLabel="Creating Concept"
        >
          Create Concept Set
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={closeCreateConceptModal}
          isDisabled={isCreateConceptSubmitting}
        >
          Cancel
        </Button>
      </div>
    </FormProvider>
  );

  // --- Concept edit state and handlers ---
  const [selectedDomain, setSelectedDomain] = useState("");
  const [selectedSystem, setSelectedSystem] = useState("");
  const [isEditConceptSubmitting, setIsEditConceptSubmitting] = useState(false);
  const [isEditConceptUploading, setIsEditConceptUploading] = useState(false);
  const [isEditConceptModalOpen, setIsEditConceptModalOpen] = useState(false);
  const [updateConceptApi] = useUpdateConceptSetMutation();
  const editConceptForm = useForm();

  const { data: conceptSetsResponse } = useGetConceptSetsQuery();
  const conceptSets = useMemo(
    () => conceptSetsResponse ?? [],
    [conceptSetsResponse]
  );
  const [searchConceptSets] = useLazySearchConceptSetsByConditionQuery();
  const [totalDefinitionCounts, dispatchTotalDefinitionCount] = useReducer(
    totalDefinitionCountsReducer,
    {}
  );
  const [newSetsPagination, setNewSetsPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  useEffect(() => {
    let isActive = true;

    const loadTotalDefinitionCounts = async () => {
      if (conceptSets.length === 0) {
        dispatchTotalDefinitionCount({ type: "reset" });
        return;
      }

      await Promise.all(
        conceptSets.map(async (conceptSet) => {
          const domainValue = conceptSet.name?.replace(CONCEPTS_SUFFIX, "");
          let totalDefinitionCount = "-";

          if (conceptSet._id && domainValue && conceptSet.system) {
            try {
              const result = await searchConceptSets(
                buildConceptSetSearchCondition(
                  { value: domainValue },
                  { label: conceptSet.system }
                )
              ).unwrap();
              totalDefinitionCount = result?.[0]?.values?.length ?? 0;
            } catch (_error) {
              totalDefinitionCount = "-";
            }
          }

          if (isActive && conceptSet._id) {
            dispatchTotalDefinitionCount({
              type: "set",
              conceptSetId: conceptSet._id,
              totalDefinitionCount,
            });
          }
        })
      );
    };

    loadTotalDefinitionCounts();

    return () => {
      isActive = false;
    };
  }, [conceptSets, searchConceptSets]);

  const [pendingConceptSetDownload, setPendingConceptSetDownload] =
    useState(null);

  const resetEditConceptState = () => {
    setSelectedDomain("");
    setSelectedSystem("");
    editConceptForm.reset();
    setIsEditConceptSubmitting(false);
    setIsEditConceptUploading(false);
  };

  const canEditConceptSubmit =
    isEditConceptSetSelectionComplete(selectedDomain, selectedSystem) &&
    !isEditConceptSubmitting;

  const { rows: editConceptSetRows, isFetching: isEditConceptSetFetching } =
    useConceptSetSearchResults(selectedDomain, selectedSystem);

  // --- Concept view state and handlers ---
  const [viewSelectedDomain, setViewSelectedDomain] = useState("");
  const [viewSelectedSystem, setViewSelectedSystem] = useState("");
  const [isViewConceptModalOpen, setIsViewConceptModalOpen] = useState(false);

  const downloadConceptSetCsv = (
    rows,
    domainValue,
    systemLabel,
    filenamePrefix = "concept-sets"
  ) => {
    downloadCsv({
      columns: DOWNLOAD_CONCEPT_SET_COLUMNS,
      data: buildConceptSetDownloadRows(rows, domainValue),
      filename: `${filenamePrefix}-${systemLabel}-${domainValue}`,
      columnAccessorKey: "accessorKey",
    });
  };

  const handleDownloadViewConceptSet = () =>
    downloadConceptSetCsv(
      viewConceptSetRows,
      viewSelectedDomain.value,
      viewSelectedSystem.label
    );

  const handleDownloadEditConceptSet = () =>
    downloadConceptSetCsv(
      editConceptSetRows,
      selectedDomain.value,
      selectedSystem.label
    );

  const handleDownloadEditConceptTemplate = () =>
    downloadConceptSetCsv(
      [],
      selectedDomain.value,
      selectedSystem.label,
      "concept-set-template"
    );

  const handleDownloadCreateConceptTemplate = () => {
    const trimmedDomain = domain?.trim();
    const trimmedSystem = system?.trim();

    if (!trimmedDomain || !trimmedSystem) return;

    downloadConceptSetCsv(
      [],
      trimmedDomain,
      trimmedSystem,
      "concept-set-template"
    );
  };

  const getConceptSetSelection = (conceptSet) => {
    const domainValue = conceptSet.name?.replace(CONCEPTS_SUFFIX, "");
    return {
      domain: { label: domainValue, value: domainValue },
      system: {
        id: conceptSet._id,
        label: conceptSet.system,
        value: conceptSet._id,
      },
    };
  };

  const handleViewConceptSet = (conceptSet) => {
    const selection = getConceptSetSelection(conceptSet);
    setViewSelectedDomain(selection.domain);
    setViewSelectedSystem(selection.system);
    setIsViewConceptModalOpen(true);
  };

  const handleEditConceptSet = (conceptSet) => {
    const selection = getConceptSetSelection(conceptSet);
    setSelectedDomain(selection.domain);
    setSelectedSystem(selection.system);
    setIsEditConceptModalOpen(true);
  };

  const handleDownloadConceptSet = (conceptSet) => {
    const selection = getConceptSetSelection(conceptSet);
    setPendingConceptSetDownload(selection);
    setViewSelectedDomain(selection.domain);
    setViewSelectedSystem(selection.system);
  };

  const ConceptSetsDataTableProps = useDataTable({
    initialData: [],
    initialColumns: getConceptSetColumns({
      onView: handleViewConceptSet,
      onEdit: handleEditConceptSet,
      onDownload: handleDownloadConceptSet,
    }),
    rowIdKey: "_id",
    tableConfig: {
      enableColumnFilters: false,
      enableSorting: true,
      initialState: {
        sorting: [{ id: "domain", desc: false }],
      },
      state: { pagination: newSetsPagination },
      onPaginationChange: setNewSetsPagination,
    },
    paginationConfig: {
      enablePagination: true,
      pageSizeOptions: [10, 25, 50],
    },
  });

  useEffect(() => {
    if (typeof ConceptSetsDataTableProps.setData === "function") {
      ConceptSetsDataTableProps.setData(
        buildConceptSetTableRows(conceptSets, totalDefinitionCounts),
        true
      );
    }
  }, [conceptSets, totalDefinitionCounts]);

  const {
    condition: viewCondition,
    rows: viewConceptSetRows,
    isFetching: isViewConceptSetFetching,
  } = useConceptSetSearchResults(viewSelectedDomain, viewSelectedSystem);

  useEffect(() => {
    if (!pendingConceptSetDownload || isViewConceptSetFetching) return;

    const isSelectedConceptSet =
      viewSelectedDomain?.value === pendingConceptSetDownload.domain.value &&
      viewSelectedSystem?.value === pendingConceptSetDownload.system.value;

    if (!isSelectedConceptSet) return;

    if (viewConceptSetRows.length > 0) {
      downloadConceptSetCsv(
        viewConceptSetRows,
        pendingConceptSetDownload.domain.value,
        pendingConceptSetDownload.system.label
      );
    } else {
      toast.show({
        title: "Unable to download concept set",
        type: "error",
        message: "No concept definitions were found for this concept set.",
      });
    }

    setPendingConceptSetDownload(null);
  }, [
    downloadConceptSetCsv,
    isViewConceptSetFetching,
    pendingConceptSetDownload,
    toast,
    viewConceptSetRows,
    viewSelectedDomain,
    viewSelectedSystem,
  ]);

  const [viewPagination, setViewPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const viewDataTableProps = useDataTable({
    initialData: [],
    initialColumns: VIEW_CONCEPT_SET_COLUMNS,
    rowIdKey: "code",
    tableConfig: {
      enableColumnFilters: false,
      enableSorting: true,
      state: { pagination: viewPagination },
      onPaginationChange: setViewPagination,
    },
    paginationConfig: {
      enablePagination: true,
      pageSizeOptions: [10, 25, 50],
    },
  });

  useEffect(() => {
    if (typeof viewDataTableProps.setData === "function") {
      viewDataTableProps.setData(viewConceptSetRows, true);
    }
  }, [viewConceptSetRows]);

  const editConcept = async (file) => {
    toast.show({
      id: "concept-set",
      isLoading: true,
      ariaLoadingLabel: "editing concept set",
      title: "Editing concept set",
      type: "info",
      message: "Uploading and processing data, please wait...",
      autoClose: false,
      withCloseButton: false,
    });

    setIsEditConceptSubmitting(true);

    const requestBody = buildEditConceptSetUpdateRequest(
      file,
      selectedDomain,
      selectedSystem
    );

    let results;
    try {
      results = await updateConceptApi(requestBody).unwrap();
    } catch (err) {
      updateConceptToast(err, {
        isError: true,
        domainValue: selectedDomain.value,
        systemValue: selectedSystem.label,
        defaultSuccessMessage: "Concept set updated successfully.",
      });
      return;
    }
    setIsEditConceptModalOpen(false);
    updateConceptToast(results, {
      isError: false,
      domainValue: selectedDomain.value,
      systemValue: selectedSystem.label,
      defaultSuccessMessage: "Concept set updated successfully.",
    });
  };

  const handleEditConceptSubmit = (data) => {
    if (!isEditConceptSetSelectionComplete(selectedDomain, selectedSystem)) {
      toast.show({
        id: "concept-set-validation",
        title: "Missing concept set selection",
        type: "error",
        message:
          "Select a domain and system before uploading concept definitions.",
        autoClose: false,
      });
      return;
    }

    const file = data.fileUpload?.[0];

    if (!file || file.size === 0) {
      window.alert("Chosen file is empty- please upload a valid .csv file.");
      return;
    }
    setIsEditConceptUploading(true);
    editConcept(file);
  };

  if (!isAdmin) {
    return (
      <Layout>
        <PageBodyIntro
          title="Administration"
          breadcrumbs={[
            { label: PATHS.admin.displayName, href: PATHS.admin.url },
          ]}
        />
        <RouteDisplay pageRouteDisplay={[PATHS.admin]} />
        <PageBody>
          <Card className="p-4 mt-3">
            <Text fontSize="lg" fontWeight="bold" asElement="div">
              This account has insufficient permission to view this page.
            </Text>
          </Card>
        </PageBody>
      </Layout>
    );
  }

  return (
    <Layout>
      <PageBodyIntro
        className="mint-admin-intro"
        title="Administration"
        text="Admin is where Clients, Domains, and other platform-wide settings are managed in MINT. Use the tabs below to view and update each one."
      />
      <PageBody fullWidth>
        <Tabs className="mint-admin-tabs" title="Administration">
          <Tabs.Tab label="Clients">
            <div className="row mb-3 mt-3 align-items-start">
              <h2 className="mb-3">Clients</h2>
              <Text asElement="p">
                Review and manage the client records available to this
                application. Search the client list, review when records were
                last updated, add new clients, and keep names and descriptions
                accurate. Retire clients that are no longer active while keeping
                their records available for reference.
              </Text>
            </div>
            {isClientListError ? (
              <Alert status="error" title="Unable to load clients">
                <Text asElement="p">
                  There was a problem loading the client list. Please try again.
                </Text>
                <Button onClick={refetchClientList}>Retry</Button>
              </Alert>
            ) : (
              <DataTable hideHeader tableState={clientDataTableProps}>
                <div className="ms-3 me-3">
                  <div className="row mb-3 mt-3 align-items-start">
                    <div className="w-50">
                      <DataTable.GlobalFilter
                        className="client-global-filter"
                        searchMode="onChange"
                      />
                    </div>
                    <div className="w-50 d-flex justify-content-end">
                      <Button
                        icon={{ icon: "add", position: "leading" }}
                        onClick={() => setIsCreateClientModalOpen(true)}
                      >
                        Add New Client
                      </Button>
                    </div>
                  </div>
                </div>
                <DataTable.Table
                  isLoading={isClientListLoading || isClientListFetching}
                  rowConfig={{
                    highlightConfig: {
                      rowsHighlighted: Object.keys(
                        clientDataTableProps.editRowState.rowsInEditMode
                      ).map((rowId) => ({
                        rowId,
                        color: "#e7f1ff",
                      })),
                    },
                  }}
                />
                <div className="ps-2 ms-3 mt-3 mb-3 me-3">
                  <DataTable.Pagination showPageSizeDropdown />
                </div>
              </DataTable>
            )}
            <ModalDialog
              header="Add New Client"
              isOpen={isCreateClientModalOpen}
              onClose={closeCreateClientModal}
              size="md"
            >
              <form onSubmit={handleCreateClientSubmit} noValidate>
                <div className="mb-3">
                  <TextInput
                    id="modal-create-client-name"
                    label="Client Name"
                    placeholder="Client Name"
                    isRequired
                    value={createClientFormValues.clientName}
                    isDisabled={isCreateClientSubmitting}
                    onChange={handleCreateClientChange("clientName")}
                    model="lowercase"
                    formatter={(value) => value.toLowerCase()}
                  />
                  {createClientErrors.clientName ? (
                    <p className="text-danger small mt-1 mb-0 d-flex align-items-center gap-1">
                      <FontAwesomeIcon
                        aria-hidden="true"
                        icon={solid("circle-exclamation")}
                      />
                      {createClientErrors.clientName}
                    </p>
                  ) : null}
                </div>
                <div className="mb-4">
                  <TextInput
                    id="modal-create-client-description"
                    label="Description"
                    placeholder="Brief description"
                    value={createClientFormValues.description}
                    isDisabled={isCreateClientSubmitting}
                    onChange={handleCreateClientChange("description")}
                  />
                </div>
                <div className="d-flex gap-2">
                  <Button
                    type="submit"
                    isDisabled={!canCreateClientSubmit}
                    isLoading={isCreateClientSubmitting}
                    ariaLoadingLabel="Creating Client"
                  >
                    Add Client
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={closeCreateClientModal}
                    isDisabled={isCreateClientSubmitting}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </ModalDialog>
            <ModalDialog
              isOpen={Boolean(clientPendingRetirement)}
              onClose={() => setClientPendingRetirement(null)}
              size="sm"
              header={`Retire ${clientPendingRetirement?.name}?`}
            >
              <Text asElement="p">
                Retiring a client will mark it as ‘Retired.’ This action will
                affect all associated records.
                <Text asElement="span" className="d-block mt-2">
                  This action cannot be undone.
                </Text>
              </Text>
              <div className="d-flex gap-2 justify-content-end">
                <Button
                  type="button"
                  color="destructive"
                  size="sm"
                  onClick={confirmGridRetireClient}
                  isLoading={isRetiringClient}
                  ariaLoadingLabel="Retiring Client"
                >
                  Retire Client
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setClientPendingRetirement(null)}
                  isDisabled={isRetiringClient}
                >
                  Cancel
                </Button>
              </div>
            </ModalDialog>
          </Tabs.Tab>

          <Tabs.Tab label="Concept Sets">
            <div className="row mb-3 mt-3 align-items-start">
              <h2 className="mb-3">Concept Sets</h2>
              <Text asElement="p">
                Review the concept sets available to this application. Browse
                definition counts, use the search field to find concept sets by
                domain or system, preview code definitions, replace or download
                concept sets, and add a new concept set. Use the actions in each
                row to manage the selected concept set and its code definitions.
              </Text>
            </div>

            <div className="row mt-3 justify-content-center">
              <div className="col-12">
                <DataTable hideHeader tableState={ConceptSetsDataTableProps}>
                  <div className="ms-3 me-3">
                    <div className="row mb-3 mt-3 align-items-start">
                      <div className="w-50">
                        <DataTable.GlobalFilter
                          className="client-global-filter"
                          searchMode="onChange"
                        />
                      </div>
                      <div className="w-50 d-flex justify-content-end">
                        <Button
                          icon={{ icon: "add", position: "leading" }}
                          onClick={() => setIsCreateConceptModalOpen(true)}
                        >
                          Add New Concept Set
                        </Button>
                      </div>
                    </div>
                  </div>

                  <DataTable.Table />
                  <div className="ps-2 ms-3 mt-3 mb-3 me-3">
                    <DataTable.Pagination showPageSizeDropdown />
                  </div>
                </DataTable>
              </div>
            </div>
            <ModalDialog
              isOpen={isViewConceptModalOpen}
              onClose={() => setIsViewConceptModalOpen(false)}
              size="70%"
              header="Concept Set Preview"
            >
              <div className="d-flex w-100 align-items-center gap-3">
                <Text>
                  Domain: {viewSelectedDomain.label}
                  <br />
                  System: {viewSelectedSystem.label}
                  <br />
                  Code Definitions: {viewConceptSetRows.length}
                </Text>
              </div>
              {viewCondition && (
                <DataTable
                  title={`${viewSelectedDomain.label} Concept Definitions`}
                  description={`Code definitions for the ${viewSelectedDomain.label} concept set using the ${viewSelectedSystem.label} system.`}
                  headingLevel={2}
                  tableState={viewDataTableProps}
                  hideHeader
                >
                  <DataTable.SlotWrapper>
                    <div className="d-flex w-100 align-items-center gap-3">
                      <DataTable.GlobalFilter
                        className="client-global-filter"
                        searchMode="onChange"
                      />
                      <Button
                        className="ms-auto"
                        onClick={handleDownloadViewConceptSet}
                        isDisabled={
                          isViewConceptSetFetching ||
                          viewConceptSetRows.length === 0
                        }
                        variant="outline"
                      >
                        Download CSV
                      </Button>
                    </div>
                  </DataTable.SlotWrapper>

                  <DataTable.Table isLoading={isViewConceptSetFetching} />
                  <div className="ps-2 ms-3 mt-3 mb-3 me-3">
                    <DataTable.Pagination showPageSizeDropdown />
                  </div>
                </DataTable>
              )}
            </ModalDialog>
            <ModalDialog
              header="Create New Concept Set"
              isOpen={isCreateConceptModalOpen}
              onClose={closeCreateConceptModal}
              size="md"
            >
              {renderCreateConceptForm()}
            </ModalDialog>
            <ModalDialog
              header="Replace Code Definitions"
              isOpen={isEditConceptModalOpen}
              onClose={() => setIsEditConceptModalOpen(false)}
              size="md"
            >
              <div className="mt-1">
                <Text>
                  Upload a new file to replace the existing Definitions in this
                  Concept Set. This action cannot be undone.
                </Text>
              </div>
              <div className="d-flex align-items-center gap-2 mt-1">
                <span>Use the</span>
                <Link
                  type="button"
                  onClick={handleDownloadEditConceptTemplate}
                  isDisabled={
                    !isEditConceptSetSelectionComplete(
                      selectedDomain,
                      selectedSystem
                    )
                  }
                  variant="underlined"
                >
                  Concept Set Template
                </Link>
                <span>
                  if you are unsure of the required data and formatting.
                </span>
              </div>

              <div className="d-flex align-items-center gap-2 mb-3">
                <span>Use the</span>
                <Link
                  type="button"
                  onClick={handleDownloadEditConceptSet}
                  isDisabled={
                    isEditConceptSetFetching || editConceptSetRows.length === 0
                  }
                  variant="underlined"
                >
                  Download Existing CSV
                </Link>
                <span>to download the existing concept set.</span>
              </div>

              <FormProvider
                state={editConceptForm}
                onSubmit={handleEditConceptSubmit}
              >
                <div className="mb-3">
                  <FileUpload
                    label="Upload Code Definitions"
                    isRequired
                    fileTypes={conceptFileTypesAccepted}
                    isUploading={isEditConceptUploading}
                    maxFiles={1}
                    model="fileUpload"
                    uploadMessage="Drag your .csv file here"
                    subText="Use .csv only"
                  />
                </div>
                <div className="d-flex gap-2 mt-4">
                  <Button
                    ariaLoadingLabel="Edit Concept"
                    isDisabled={!canEditConceptSubmit}
                    isLoading={isEditConceptSubmitting}
                    type="submit"
                  >
                    Replace Definitions
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsEditConceptModalOpen(false)}
                    isDisabled={isEditConceptSubmitting}
                  >
                    Cancel
                  </Button>
                </div>
              </FormProvider>
            </ModalDialog>
          </Tabs.Tab>
          <Tabs.Tab label="Feature Settings">
            <div className="row mb-3 mt-3 align-items-start">
              <div className="w-50">
                <h2 className="mb-3">Feature Settings</h2>
                <Text asElement="p">
                  There are currently no experimental features available.
                </Text>
              </div>
            </div>
          </Tabs.Tab>
        </Tabs>
      </PageBody>
    </Layout>
  );
}
